// "Your sessions" on Your account (#807): where the person is signed in, over
// `GET /auth/sessions`, and signing out everywhere but here over
// `POST /auth/sessions/revoke-others` (`api/account.ts`). This session is the one
// `GET /auth/session` names; the route keeps it, so the list read after holds it alone.

import { useCallback, useEffect, useRef, useState } from "react";

import type { SessionActions, SessionRow } from "../api/account";
import { messageOf } from "../api/http";
import { Chip, Notice } from "./chrome";

const day = (stamp: string): string => stamp.slice(0, 10);

/** A browser and system a person recognises, from the user agent the session was made with. */
export function browserOf(userAgent: string): string {
  const browser =
    /Edg\//.test(userAgent) ? "Edge"
    : /Firefox\//.test(userAgent) ? "Firefox"
    : /Chrome\//.test(userAgent) ? "Chrome"
    : /Safari\//.test(userAgent) ? "Safari"
    : /curl|python|node|go-http/i.test(userAgent) ? "A script"
    : null;
  const system =
    /iPhone/.test(userAgent) ? "iPhone"
    : /iPad/.test(userAgent) ? "iPad"
    : /Android/.test(userAgent) ? "Android"
    : /Windows/.test(userAgent) ? "Windows"
    : /Mac OS X|Macintosh/.test(userAgent) ? "Mac"
    : /Linux/.test(userAgent) ? "Linux"
    : null;
  if (browser === null && system === null) return "Unknown browser";
  if (browser === null) return `A browser on ${system}`;
  return system === null ? browser : `${browser} on ${system}`;
}

const signedOut = (count: number): string =>
  count === 1 ? "1 other session was signed out." : `${count} other sessions were signed out.`;

export function YourSessions({
  actions,
  currentId,
}: {
  actions: SessionActions;
  /** This session's id, to mark it in the list. */
  currentId: string | null;
}) {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ended, setEnded] = useState<number | null>(null);
  // The button goes once no other session is left, so focus returns to the heading (#809).
  const heading = useRef<HTMLHeadingElement>(null);

  const live = useRef(actions);
  live.current = actions;
  const refresh = useCallback(async () => {
    try {
      setRows(await live.current.list());
      setError(null);
    } catch (failure) {
      setError(messageOf(failure));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOutOthers = async () => {
    setBusy(true);
    setEnded(null);
    setError(null);
    try {
      setEnded(await actions.revokeOthers());
      await refresh();
      heading.current?.focus();
    } catch (failure) {
      setError(`${messageOf(failure)} No session was signed out.`);
    } finally {
      setBusy(false);
    }
  };

  const others = rows?.filter((row) => row.session_id !== currentId).length ?? 0;
  return (
    <section className="panel" id="sessions" aria-label="Your sessions">
      <div className="panel__head">
        <h2 ref={heading} tabIndex={-1}>
          Your sessions
        </h2>
        {rows === null ? null : <Chip>{rows.length} signed in</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">Everywhere you are signed in, in any workspace.</p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {ended === null ? null : <Notice tone="good">{signedOut(ended)}</Notice>}
        {rows === null && error === null ? <p className="muted">Reading your sessions…</p> : null}
      </div>
      {rows === null || rows.length === 0 ? null : (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Browser</th>
                <th scope="col">Address</th>
                <th scope="col">Signed in</th>
                <th scope="col">Last active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.session_id}>
                  <td>
                    {browserOf(row.user_agent)}{" "}
                    {row.session_id === currentId ? <Chip tone="good">This session</Chip> : null}
                  </td>
                  <td>{row.ip_address || "unknown"}</td>
                  <td>{day(row.created_at)}</td>
                  <td>{day(row.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows === null ? null : others === 0 ? (
        <div className="panel__body">
          <p className="muted">You are signed in nowhere else.</p>
        </div>
      ) : (
        <div className="panel__body actions">
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy}
            onClick={() => void signOutOthers()}
          >
            {busy ? "Signing out…" : "Sign out other sessions"}
          </button>
        </div>
      )}
    </section>
  );
}
